import {
  MAX_OPEN_WORK_TABS,
  MAX_PINNED_OPEN_WORK_TABS,
  pinnedRootOwnsPathname,
} from './open-work-tabs-state.ts';

export const OPEN_WORK_TAB_PARAMETER = 'tab';
export const ACTIVE_WORK_TAB_INDEX_PARAMETER = 'active-tab-index';
export const PINNED_WORK_TAB_PARAMETER = 'pinned-tab';
export const PRIMARY_NAVIGATION_PARAMETER = 'primary-nav';
export const SHELL_PARAMETER = 'shell';

export interface WorkspaceLocation {
  activePage: string;
  primaryNavigationHidden: boolean;
  shellHidden: boolean;
  openTabs: readonly string[];
  /** Each pinned tab is named by the record root it keeps, never by an index that shifts. */
  pinnedTabRoots: readonly string[];
}

export type WorkspaceLocationIntent =
  | { type: 'open-page'; destination: string }
  | { type: 'replace-active-page'; destination: string }
  | { type: 'replace-page-search'; search: string }
  | {
      type: 'replace-open-tabs';
      activePage: string;
      openTabs: readonly string[];
      pinnedTabRoots?: readonly string[];
    }
  | {
      type: 'set-primary-navigation';
      hidden: boolean;
    };

const LOCAL_ORIGIN = 'https://forum-and-lex.invalid';
// A page can retain several split readers, each with its own record reference and position.
const MAX_PAGE_CHARACTERS = 32_768;
const MAX_LOCATION_CHARACTERS = MAX_PAGE_CHARACTERS * MAX_OPEN_WORK_TABS * 3 + 4_096;
const OBSOLETE_SHELL_PARAMETERS = Object.freeze([
  'active-at',
  'desk',
  'deskAt',
  'navigation',
  'open-tabs',
  'sidebar',
]);
const UNSAFE_CHARACTERS = /[\u0000-\u001f\u007f]/;

function localUrl(value: unknown, maximumCharacters = MAX_LOCATION_CHARACTERS): URL | null {
  if (typeof value !== 'string' || value.length > maximumCharacters) return null;
  const path = value.trim();
  if (!path || !path.startsWith('/') || path.startsWith('//') || UNSAFE_CHARACTERS.test(path)) {
    return null;
  }
  try {
    const url = new URL(path, LOCAL_ORIGIN);
    return url.origin === LOCAL_ORIGIN ? url : null;
  } catch {
    return null;
  }
}

// Every shell link and tab normalizes the same few tab strings on each render,
// and each normalization builds URL objects. Both functions are pure, so a
// bounded cache returns identical results without repeating the parsing.
const MAX_CACHED_STRINGS = 1_000;

function boundedStringMemo(compute: (value: string) => string): (value: string) => string {
  const results = new Map<string, string>();
  return (value) => {
    const known = results.get(value);
    if (known !== undefined) return known;
    const result = compute(value);
    if (results.size >= MAX_CACHED_STRINGS) results.clear();
    results.set(value, result);
    return result;
  };
}

const normalizedPageDestination = boundedStringMemo((value) => {
  const url = localUrl(value);
  if (!url) return '';
  if (OBSOLETE_SHELL_PARAMETERS.some((parameter) => url.searchParams.has(parameter))) return '';
  url.searchParams.delete(OPEN_WORK_TAB_PARAMETER);
  url.searchParams.delete(ACTIVE_WORK_TAB_INDEX_PARAMETER);
  url.searchParams.delete(PINNED_WORK_TAB_PARAMETER);
  url.searchParams.delete(PRIMARY_NAVIGATION_PARAMETER);
  url.searchParams.delete(SHELL_PARAMETER);
  const destination = `${url.pathname}${url.search}${url.hash}`;
  return destination.length <= MAX_PAGE_CHARACTERS ? destination : '';
});

function pageDestination(value: unknown): string {
  return typeof value === 'string' ? normalizedPageDestination(value) : '';
}

const pathnameOf = boundedStringMemo(
  (destination) => localUrl(destination, MAX_PAGE_CHARACTERS)?.pathname || '',
);

function shellParametersHaveCanonicalOrder(url: URL): boolean {
  let stage: 'tab' | 'active-tab-index' | 'pinned-tab' | 'primary-nav' | 'shell' | 'page' = 'tab';
  for (const part of url.search.slice(1).split('&')) {
    if (!part) continue;
    const rawName = part.split('=', 1)[0] || '';
    let name = '';
    try {
      name = decodeURIComponent(rawName.replaceAll('+', ' '));
    } catch {
      return false;
    }
    if (name === OPEN_WORK_TAB_PARAMETER) {
      if (stage !== 'tab') return false;
      continue;
    }
    if (name === ACTIVE_WORK_TAB_INDEX_PARAMETER) {
      if (stage !== 'tab') return false;
      stage = 'active-tab-index';
      continue;
    }
    if (name === PINNED_WORK_TAB_PARAMETER) {
      if (stage !== 'tab' && stage !== 'active-tab-index' && stage !== 'pinned-tab') return false;
      stage = 'pinned-tab';
      continue;
    }
    if (name === PRIMARY_NAVIGATION_PARAMETER) {
      if (stage === 'shell' || stage === 'page') return false;
      stage = 'primary-nav';
      continue;
    }
    if (name === SHELL_PARAMETER) {
      if (stage === 'page') return false;
      stage = 'shell';
      continue;
    }
    stage = 'page';
  }
  return true;
}

// Primary navigation is hidden unless the address shows it: almost every page is read that way,
// so the usual address needs no parameter.
function readPrimaryNavigationHidden(url: URL): boolean | undefined {
  const values = url.searchParams.getAll(PRIMARY_NAVIGATION_PARAMETER);
  if (values.length === 0) return true;
  return values.length === 1 && values[0] === 'shown' ? false : undefined;
}

function readHiddenParameter(url: URL, parameter: string): boolean | undefined {
  const values = url.searchParams.getAll(parameter);
  if (values.length === 0) return false;
  return values.length === 1 && values[0] === 'hidden' ? true : undefined;
}

function readActiveTabIndex(url: URL, inactiveTabCount: number): number | undefined {
  const values = url.searchParams.getAll(ACTIVE_WORK_TAB_INDEX_PARAMETER);
  if (inactiveTabCount === 0) return values.length === 0 ? 0 : undefined;
  if (values.length !== 1 || !/^(?:0|[1-9]\d*)$/.test(values[0] || '')) return undefined;
  const index = Number(values[0]);
  return Number.isSafeInteger(index) && index <= inactiveTabCount ? index : undefined;
}

function isPinnedPathname(pathname: string, pinnedRoots: readonly string[]): boolean {
  return pinnedRoots.some((root) => pinnedRootOwnsPathname(root, pathname));
}

function normalizedOpenTabs(
  activePage: string,
  candidates: readonly unknown[],
  pinnedRoots: readonly string[],
): string[] {
  const activePathname = pathnameOf(activePage);
  if (!activePathname) return [];

  const tabs: string[] = [];
  const indexByPathname = new Map<string, number>();
  for (const candidate of candidates) {
    const destination = pageDestination(candidate);
    const pathname = pathnameOf(destination);
    if (!destination || !pathname) continue;
    const existingIndex = indexByPathname.get(pathname);
    if (existingIndex === undefined) {
      indexByPathname.set(pathname, tabs.length);
      tabs.push(destination);
    } else {
      tabs[existingIndex] = destination;
    }
  }

  const activeIndex = indexByPathname.get(activePathname);
  if (activeIndex === undefined) tabs.push(activePage);
  else tabs[activeIndex] = activePage;
  // Pinned tabs are never evicted; at most MAX_PINNED_OPEN_WORK_TABS leaves one
  // unpinned tab to make room.
  while (tabs.length > MAX_OPEN_WORK_TABS) {
    const removableIndex = tabs.findIndex((tab) => {
      const pathname = pathnameOf(tab);
      return pathname !== activePathname && !isPinnedPathname(pathname, pinnedRoots);
    });
    tabs.splice(removableIndex, 1);
  }
  return tabs;
}

function canonicalPinnedRoot(value: unknown): string {
  if (typeof value !== 'string' || value === '/') return '';
  const url = localUrl(value, MAX_PAGE_CHARACTERS);
  return url?.pathname === value && !value.endsWith('/') ? value : '';
}

// Each root must keep at least one open tab, and no tab may belong to two roots.
function pinnedRootsKeepingOpenTabs(
  roots: readonly unknown[],
  openTabs: readonly string[],
): string[] | undefined {
  const pinnedRoots: string[] = [];
  const pinnedPathnames = new Set<string>();
  for (const value of roots) {
    const root = canonicalPinnedRoot(value);
    if (!root || pinnedRoots.includes(root)) return undefined;
    const keptPathnames = openTabs
      .map((tab) => pathnameOf(tab))
      .filter((pathname) => pinnedRootOwnsPathname(root, pathname));
    if (!keptPathnames.length || keptPathnames.some((pathname) => pinnedPathnames.has(pathname))) {
      return undefined;
    }
    for (const pathname of keptPathnames) pinnedPathnames.add(pathname);
    pinnedRoots.push(root);
  }
  return pinnedRoots.length <= MAX_PINNED_OPEN_WORK_TABS ? pinnedRoots : undefined;
}

export function parseWorkspaceLocation(value: unknown): WorkspaceLocation | null {
  const url = localUrl(value);
  if (!url || !shellParametersHaveCanonicalOrder(url)) return null;
  if (OBSOLETE_SHELL_PARAMETERS.some((parameter) => url.searchParams.has(parameter))) return null;

  const primaryNavigationHidden = readPrimaryNavigationHidden(url);
  const shellHidden = readHiddenParameter(url, SHELL_PARAMETER);
  if (primaryNavigationHidden === undefined || shellHidden === undefined) return null;
  const activePage = pageDestination(value);
  if (!activePage) return null;

  const tokens = url.searchParams.getAll(OPEN_WORK_TAB_PARAMETER);
  const activeTabIndex = readActiveTabIndex(url, tokens.length);
  if (activeTabIndex === undefined) return null;
  if (tokens.length === 0) {
    const pinnedTabRoots = pinnedRootsKeepingOpenTabs(
      url.searchParams.getAll(PINNED_WORK_TAB_PARAMETER),
      [activePage],
    );
    if (!pinnedTabRoots) return null;
    return {
      activePage,
      primaryNavigationHidden,
      shellHidden,
      openTabs: [activePage],
      pinnedTabRoots,
    };
  }
  if (tokens.length >= MAX_OPEN_WORK_TABS) return null;

  const activePathname = url.pathname;
  const inactiveTabs: string[] = [];
  const pathnames = new Set<string>();
  for (const token of tokens) {
    const tokenUrl = localUrl(token, MAX_PAGE_CHARACTERS);
    if (
      !tokenUrl ||
      tokenUrl.searchParams.has(OPEN_WORK_TAB_PARAMETER) ||
      tokenUrl.searchParams.has(ACTIVE_WORK_TAB_INDEX_PARAMETER) ||
      tokenUrl.searchParams.has(PINNED_WORK_TAB_PARAMETER) ||
      tokenUrl.searchParams.has(PRIMARY_NAVIGATION_PARAMETER) ||
      tokenUrl.searchParams.has(SHELL_PARAMETER) ||
      OBSOLETE_SHELL_PARAMETERS.some((parameter) => tokenUrl.searchParams.has(parameter))
    ) {
      return null;
    }
    const tokenPage = pageDestination(token);
    const tokenPathname = tokenUrl.pathname;
    if (!tokenPage || pathnames.has(tokenPathname)) return null;
    pathnames.add(tokenPathname);
    if (tokenPathname === activePathname) return null;
    inactiveTabs.push(tokenPage);
  }
  const openTabs = [...inactiveTabs];
  openTabs.splice(activeTabIndex, 0, activePage);
  const pinnedTabRoots = pinnedRootsKeepingOpenTabs(
    url.searchParams.getAll(PINNED_WORK_TAB_PARAMETER),
    openTabs,
  );
  if (!pinnedTabRoots) return null;
  return { activePage, primaryNavigationHidden, shellHidden, openTabs, pinnedTabRoots };
}

export function workspaceLocationIsRecognized(value: unknown): boolean {
  return parseWorkspaceLocation(value) !== null;
}

function describedParameters(parameters: readonly string[]): string {
  return parameters.map((parameter) => `"${parameter}"`).join(', ');
}

export function workspaceLocationFailureDescription(value: unknown): string | null {
  if (parseWorkspaceLocation(value)) return null;
  const url = localUrl(value);
  if (!url) {
    return 'This URL is not a safe, bounded in-application path. Open the destination through the current application navigation before retrying.';
  }

  const obsoleteParameters = OBSOLETE_SHELL_PARAMETERS.filter((parameter) =>
    url.searchParams.has(parameter),
  );
  if (obsoleteParameters.length > 0) {
    return `This URL uses obsolete workspace parameter${obsoleteParameters.length === 1 ? '' : 's'} ${describedParameters(obsoleteParameters)}. Open the page through the current application navigation and replace the saved link before retrying.`;
  }
  if (!shellParametersHaveCanonicalOrder(url)) {
    return 'This URL places workspace parameters in a noncanonical order. Open the page through the current application navigation and replace the saved link before retrying.';
  }

  if (readPrimaryNavigationHidden(url) === undefined) {
    return `The "${PRIMARY_NAVIGATION_PARAMETER}" workspace parameter must be omitted, which hides primary navigation, or have the single value "shown". Remove "${PRIMARY_NAVIGATION_PARAMETER}=hidden" from older links.`;
  }
  if (readHiddenParameter(url, SHELL_PARAMETER) === undefined) {
    return `The "${SHELL_PARAMETER}" workspace parameter must be omitted or have the single value "hidden". Open the page through the current application navigation before retrying.`;
  }

  const tabTokens = url.searchParams.getAll(OPEN_WORK_TAB_PARAMETER);
  for (const token of tabTokens) {
    const tokenUrl = localUrl(token, MAX_PAGE_CHARACTERS);
    if (!tokenUrl) {
      return `The workspace "tab" entry "${token}" is not a canonical in-application path. Open that page through the current application navigation before retrying.`;
    }
    const obsoleteTabParameters = OBSOLETE_SHELL_PARAMETERS.filter((parameter) =>
      tokenUrl.searchParams.has(parameter),
    );
    if (obsoleteTabParameters.length > 0) {
      return `An inactive workspace tab uses obsolete parameter${obsoleteTabParameters.length === 1 ? '' : 's'} ${describedParameters(obsoleteTabParameters)}. Reopen that tab through the current application navigation before retrying.`;
    }
    if (
      tokenUrl.searchParams.has(OPEN_WORK_TAB_PARAMETER) ||
      tokenUrl.searchParams.has(ACTIVE_WORK_TAB_INDEX_PARAMETER) ||
      tokenUrl.searchParams.has(PINNED_WORK_TAB_PARAMETER) ||
      tokenUrl.searchParams.has(PRIMARY_NAVIGATION_PARAMETER) ||
      tokenUrl.searchParams.has(SHELL_PARAMETER)
    ) {
      return 'An inactive workspace tab contains nested shell state. Reopen that tab through the current application navigation before retrying.';
    }
  }

  const activeIndexValues = url.searchParams.getAll(ACTIVE_WORK_TAB_INDEX_PARAMETER);
  if (tabTokens.length > 0 && activeIndexValues.length === 0) {
    return 'This multi-tab URL is missing its canonical "active-tab-index" value. Open the page through the current application navigation before retrying.';
  }
  if (
    activeIndexValues.length > 1 ||
    activeIndexValues.some((value) => !/^(?:0|[1-9]\d*)$/.test(value))
  ) {
    return 'The "active-tab-index" workspace parameter is not canonical. Open the page through the current application navigation before retrying.';
  }
  if (url.searchParams.has(PINNED_WORK_TAB_PARAMETER)) {
    return `Each "pinned-tab" value must be a distinct record path that keeps one of the open tabs, with at most ${MAX_PINNED_OPEN_WORK_TABS} pinned. Open the page through the current application navigation and pin the tabs again.`;
  }

  return 'This URL does not satisfy the current workspace format. Open the destination through the current application navigation and replace the saved link before retrying.';
}

// A root whose tabs were closed or evicted is no longer pinned.
function pinnedRootsStillOpen(roots: readonly unknown[], openTabs: readonly string[]): string[] {
  const pinnedRoots: string[] = [];
  for (const root of roots) {
    if (pinnedRoots.length >= MAX_PINNED_OPEN_WORK_TABS) break;
    if (pinnedRootsKeepingOpenTabs([...pinnedRoots, root], openTabs)) {
      pinnedRoots.push(root as string);
    }
  }
  return pinnedRoots;
}

function withActivePage(
  state: WorkspaceLocation,
  activePage: string,
  openTabs: readonly unknown[],
  pinnedRoots: readonly unknown[] = state.pinnedTabRoots,
): WorkspaceLocation | null {
  const normalizedActivePage = pageDestination(activePage);
  if (!normalizedActivePage) return null;
  const candidateRoots = pinnedRoots.filter((root): root is string =>
    Boolean(canonicalPinnedRoot(root)),
  );
  const normalizedTabs = normalizedOpenTabs(normalizedActivePage, openTabs, candidateRoots);
  return {
    ...state,
    activePage: normalizedActivePage,
    openTabs: normalizedTabs,
    pinnedTabRoots: pinnedRootsStillOpen(candidateRoots, normalizedTabs),
  };
}

export function applyWorkspaceLocationIntent(
  state: WorkspaceLocation,
  intent: WorkspaceLocationIntent,
): WorkspaceLocation | null {
  if (intent.type === 'set-primary-navigation') {
    return { ...state, primaryNavigationHidden: intent.hidden };
  }

  if (intent.type === 'replace-page-search') {
    const activeUrl = localUrl(state.activePage, MAX_PAGE_CHARACTERS);
    if (!activeUrl) return null;
    activeUrl.search = intent.search.startsWith('?') ? intent.search.slice(1) : intent.search;
    return withActivePage(
      state,
      pageDestination(`${activeUrl.pathname}${activeUrl.search}${activeUrl.hash}`),
      state.openTabs,
    );
  }

  if (intent.type === 'replace-open-tabs') {
    return withActivePage(state, intent.activePage, intent.openTabs, intent.pinnedTabRoots);
  }

  const destination = pageDestination(intent.destination);
  if (!destination) return null;
  // A pinned tab keeps its record, so leaving the record opens the page beside it.
  const leavesPinnedRecord = state.pinnedTabRoots.some(
    (root) =>
      pinnedRootOwnsPathname(root, pathnameOf(state.activePage)) &&
      !pinnedRootOwnsPathname(root, pathnameOf(destination)),
  );
  if (intent.type === 'replace-active-page' && !leavesPinnedRecord) {
    return withActivePage(
      state,
      destination,
      state.openTabs.map((tab) => (tab === state.activePage ? destination : tab)),
    );
  }

  const destinationPathname = pathnameOf(destination);
  const existingIndex = state.openTabs.findIndex((tab) => pathnameOf(tab) === destinationPathname);
  const openTabs =
    existingIndex < 0
      ? [...state.openTabs, destination]
      : state.openTabs.map((tab, index) => (index === existingIndex ? destination : tab));
  return withActivePage(state, destination, openTabs);
}

function observableOpenTabToken(token: string): string {
  return encodeURIComponent(token)
    .replaceAll('%2F', '/')
    .replaceAll('%3F', '?')
    .replaceAll('%3D', '=')
    .replaceAll('%3A', ':');
}

export function serializeWorkspaceLocation(state: WorkspaceLocation): string {
  const normalized = withActivePage(state, state.activePage, state.openTabs, state.pinnedTabRoots);
  if (!normalized) return '';
  const activeUrl = localUrl(normalized.activePage, MAX_PAGE_CHARACTERS)!;

  const pageState = [...activeUrl.searchParams]
    .map(([key, value]) =>
      key === 'split'
        ? `split=${observableOpenTabToken(value)}`
        : new URLSearchParams([[key, value]]).toString(),
    )
    .join('&');
  const activeTabIndex = normalized.openTabs.findIndex(
    (tab) => pathnameOf(tab) === activeUrl.pathname,
  );
  const openTabs =
    normalized.openTabs.length > 1
      ? normalized.openTabs
          .filter((_, index) => index !== activeTabIndex)
          .map((tab) => `${OPEN_WORK_TAB_PARAMETER}=${observableOpenTabToken(tab)}`)
      : [];
  const activeTabIndexParameter = openTabs.length
    ? [`${ACTIVE_WORK_TAB_INDEX_PARAMETER}=${activeTabIndex}`]
    : [];
  const pinnedTabs = normalized.pinnedTabRoots.map(
    (root) => `${PINNED_WORK_TAB_PARAMETER}=${observableOpenTabToken(root)}`,
  );
  const primaryNavigation = normalized.primaryNavigationHidden
    ? []
    : [`${PRIMARY_NAVIGATION_PARAMETER}=shown`];
  const query = [
    ...openTabs,
    ...activeTabIndexParameter,
    ...pinnedTabs,
    ...primaryNavigation,
    ...(normalized.shellHidden ? [`${SHELL_PARAMETER}=hidden`] : []),
    ...(pageState ? [pageState] : []),
  ].join('&');
  return `${activeUrl.pathname}${query ? `?${query}` : ''}${activeUrl.hash}`;
}

export function workspaceLocationDestination(
  currentLocation: unknown,
  intent: WorkspaceLocationIntent,
): string {
  const current = parseWorkspaceLocation(currentLocation);
  if (!current) return '';
  const next = applyWorkspaceLocationIntent(current, intent);
  return next ? serializeWorkspaceLocation(next) : '';
}
